import { confirmDialog } from "../components/confirmHost.jsx";
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog.jsx";
import { Smartphone, Plus, RefreshCw, X as XIcon, Trash2, Pencil, Mail, MailCheck, ChevronDown, Settings as SettingsIcon, EyeOff, RotateCcw } from "lucide-react";
import GradientHeader from "../components/GradientHeader.jsx";
import { Zalozky, Field } from "../components/ui.jsx";
import { useUzkaObrazovka } from "../lib/obrazovka.js";
import Card from "../components/Card.jsx";
import Pill from "../components/Pill.jsx";
import { useApiData, apiFetch, fetchApi } from "../lib/api.js";
import { formatDateTime } from "../lib/format.js";
import { toast } from "../components/Toast.jsx";
import AiBotiKarta from "../components/AiBotiKarta.jsx";
import { lokalityProVyber } from "../lib/lokality.js";

// 13.7.2026 (owner): marketing a head-trener odstraněny z výběru (head-trener
// bude časem jen nadstavba trenéra); nutritionist = výživová specialistka.
// 16.9.2026: bc_trener je trenér najatý čistě na bootcampy.
const ROLE_OPTIONS = ["owner", "manager", "csm", "sales", "trener", "bc_trener", "nutritionist"];
const STATUS_OPTIONS = ["active", "inactive"];

function roleTone(r) {
  if (r === "owner") return "amber";
  if (r === "manager") return "blue";
  if (r === "head-trener" || r === "trener" || r === "bc_trener") return "green";
  if (r === "sales") return "violet";
  if (r === "nutritionist") return "purple";
  return "slate";
}

export default function Settings() {
  const uzka = useUzkaObrazovka();
  const [otevrenyUcet, setOtevrenyUcet] = useState(null);   // e-mail účtu s odkrytými akcemi
  // FW-153: odkaz ze Slacku otevře rovnou záložku Ignorované ve FAPI (?tab=fapi).
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => (["fapi", "boti"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "users"));
  const { data, loading, refetch } = useApiData("/api/admin-uzivatele");
  const users = data?.users || [];
  const [editFor, setEditFor] = useState(null); // null | { mode: "create" } | { mode: "edit", user }

  const tabs = [
    { id: "users", label: "Uživatelé & přístupy" },
    { id: "alerts", label: "Pravidla alertů" },
    { id: "integrations", label: "Integrace (API klíče)" },
    { id: "trial", label: "Trial automat" },
    { id: "fapi", label: "Ignorované ve FAPI" },
    // FW-BOTI (22. 10. 2026): tokeny AI botů pro členy týmu.
    { id: "boti", label: "AI boti" },
  ];

  const [sendingMail, setSendingMail] = useState("");
  const handleSendWelcome = async (u) => {
    const already = u.welcomeEmailSentAt;
    if (already && !await confirmDialog(`Přihlašovací e-mail už byl odeslán (${formatDateTime(already)}). Poslat znovu na ${u.email}?`)) return;
    setSendingMail(u.email);
    try {
      const r = await apiFetch("/api/admin-uzivatele", {
        method: "POST",
        body: JSON.stringify({ action: "sendWelcome", email: u.email }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) { toast(`Odeslání selhalo: ${j.error || `HTTP ${r.status}`}`, "error"); }
      else { refetch(); }
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); }
    finally { setSendingMail(""); }
  };

  const handleDelete = async (email) => {
    if (!await confirmDialog(`Opravdu smazat uživatele ${email}? Tato akce je nevratná.`)) return;
    try {
      const r = await apiFetch("/api/admin-uzivatele", {
        method: "POST",
        body: JSON.stringify({ action: "delete", email }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) {
        toast(`Chyba: ${j.error || `HTTP ${r.status}`}`, "error");
        return;
      }
      refetch();
    } catch (e) {
      toast(`Chyba: ${e.message}`, "error");
    }
  };

  return (
    <div className="mx-auto w-full max-w-7xl space-y-4 px-4 py-4 sm:space-y-6 sm:px-5 sm:py-6">
      {/* 9.9.2026 (fáze 4j): hlavička jako všude jinde, záložky přes `Zalozky`
          ze vzorníku — na telefonu se z nich stane rozbalovací výběr, dřív se
          čtyři dlouhé popisky posouvaly do strany a nebylo vidět, že tam jsou. */}
      <GradientHeader title="Nastavení" icon={SettingsIcon} />
      <Zalozky items={tabs.map((t) => ({ key: t.id, label: t.label }))} value={tab} onChange={setTab} stitek="Sekce" />

      {tab === "users" && (
        <>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
            <div className="flex items-start gap-3">
              <Smartphone className="w-5 h-5 flex-shrink-0 text-blue-600 mt-0.5" />
              <div>
                <div className="font-semibold mb-1">Uživatelské účty pro přihlášení do admin appce</div>
                <div className="text-xs">
                  Data jsou v PG (tabulka <code>uzivatele</code>), edituješ přímo zde.
                  Role určuje co user vidí: owner = všechno, manager = většina, sales/csm = omezené sekce, trener = jen trenérská sekce, bc_trener = jen bootcampy a vlastní fakturace, nutritionist = jen Klienti + sekce Výživa.
                </div>
              </div>
            </div>
          </div>

          <Card
            title={<>Uživatelé <span className="text-sm font-normal text-slate-500 ml-2">{users.length} {users.length === 1 ? "účet" : "účtů"}</span></>}
            action={
              <div className="flex items-center gap-2">
                <button
                  onClick={refetch}
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 rounded-xl hover:bg-slate-50 disabled:opacity-50 min-h-[44px] sm:min-h-0"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Obnovit
                </button>
                <button
                  onClick={() => setEditFor({ mode: "create" })}
                  className="flex min-h-[44px] items-center gap-2 px-3 py-1.5 bg-slate-900 text-white rounded-xl text-sm hover:bg-slate-800 sm:min-h-0"
                >
                  <Plus className="w-4 h-4" /> Přidat uživatele
                </button>
              </div>
            }
          >
            {uzka ? (
              /* 14.9.2026 (fáze 5a): na telefonu seznam řádků. Tabulka o šesti
                 sloupcích měla pevných 700 bodů a rolovala se do stran, sloupec
                 Akce byl úplně mimo obraz. Na počítači zůstává tabulka. */
              <div className="-mx-1 divide-y divide-slate-100">
                {loading && users.length === 0 && (
                  <div className="py-8 text-center text-slate-400"><RefreshCw className="inline h-5 w-5 animate-spin" /> Načítám…</div>
                )}
                {!loading && users.length === 0 && (
                  <div className="py-8 text-center text-slate-400">Žádní uživatelé. Klepni na Přidat uživatele.</div>
                )}
                {users.map((u) => {
                  const otevreny = otevrenyUcet === u.email;
                  const jeTrener = u.role === "trener";
                  const odeslano = !!u.welcomeEmailSentAt;
                  const posila = sendingMail === u.email;
                  return (
                    <div key={u.email} className="py-2.5">
                      <button type="button" onClick={() => setOtevrenyUcet(otevreny ? null : u.email)}
                        aria-expanded={otevreny} className="flex w-full items-center gap-2 px-2 text-left">
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-base font-bold leading-tight text-slate-900">
                            {u.name || <span className="font-normal italic text-slate-400">bez jména</span>}
                          </span>
                          <span className="block truncate text-[13px] text-slate-500">{u.email}</span>
                        </span>
                        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${otevreny ? "" : "-rotate-90"}`} />
                      </button>
                      <div className="mt-1.5 flex flex-wrap gap-1.5 px-2">
                        <Pill tone={roleTone(u.role)}>{u.role || "—"}</Pill>
                        <Pill tone={u.status === "active" ? "green" : "slate"}>{u.status === "active" ? "aktivní" : u.status}</Pill>
                        {u.linkedTrener && <Pill tone="slate">{u.linkedTrener}</Pill>}
                      </div>
                      {otevreny && (
                        <div className="mt-2 flex flex-wrap gap-2 px-2">
                          <button type="button" onClick={() => setEditFor({ mode: "edit", user: u })}
                            className="inline-flex min-h-[40px] items-center gap-1.5 rounded-xl border border-slate-200 px-3 text-[13px] font-semibold text-slate-700">
                            <Pencil className="h-4 w-4" /> Upravit
                          </button>
                          {jeTrener && (
                            <button type="button" onClick={() => handleSendWelcome(u)} disabled={posila}
                              title={odeslano ? `Odesláno ${formatDateTime(u.welcomeEmailSentAt)}` : "Poslat přihlašovací e-mail"}
                              className={`inline-flex min-h-[40px] items-center gap-1.5 rounded-xl border px-3 text-[13px] font-semibold disabled:opacity-50 ${
                                odeslano ? "border-emerald-200 text-emerald-700" : "border-slate-200 text-slate-700"}`}>
                              {posila ? <RefreshCw className="h-4 w-4 animate-spin" /> : odeslano ? <MailCheck className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                              {posila ? "Posílám…" : odeslano ? "Poslat znovu" : "Poslat přihlášení"}
                            </button>
                          )}
                          <button type="button" onClick={() => handleDelete(u.email)}
                            className="inline-flex min-h-[40px] items-center gap-1.5 rounded-xl border border-rose-200 px-3 text-[13px] font-semibold text-rose-600">
                            <Trash2 className="h-4 w-4" /> Smazat
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
                    <th className="py-3 px-2">Email</th>
                    <th className="py-3 px-2">Jméno</th>
                    <th className="py-3 px-2">Role</th>
                    <th className="py-3 px-2">Status</th>
                    <th className="py-3 px-2">Trenér (link)</th>
                    <th className="py-3 px-2 text-right">Akce</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && users.length === 0 && (
                    <tr><td colSpan={6} className="py-8 text-center text-slate-400"><RefreshCw className="w-5 h-5 animate-spin inline" /> Načítám...</td></tr>
                  )}
                  {!loading && users.length === 0 && (
                    <tr><td colSpan={6} className="py-8 text-center text-slate-400">Žádní uživatelé. Klikni "+ Přidat uživatele".</td></tr>
                  )}
                  {users.map((u) => (
                    <tr key={u.email} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-2 font-mono text-xs">{u.email}</td>
                      <td className="py-3 px-2 font-medium">{u.name || <span className="text-slate-400 italic">—</span>}</td>
                      <td className="py-3 px-2"><Pill tone={roleTone(u.role)}>{u.role || "—"}</Pill></td>
                      <td className="py-3 px-2"><Pill tone={u.status === "active" ? "green" : "slate"}>{u.status}</Pill></td>
                      <td className="py-3 px-2 text-xs text-slate-500">{u.linkedTrener || <span className="text-slate-300">—</span>}</td>
                      <td className="py-3 px-2 text-right whitespace-nowrap">
                        {/* 14.9.2026 (fáze 5d): akce jsou ikonová tlačítka jako všude
                            jinde v adminu, dřív to byl barevný text vedle sebe.
                            Stav odeslání nese barva obálky, popis je v nápovědě. */}
                        <div className="inline-flex items-center gap-1.5">
                          {(() => {
                            const jeTrener = u.role === "trener";
                            const odeslano = !!u.welcomeEmailSentAt;
                            const posila = sendingMail === u.email;
                            if (!jeTrener) {
                              return (
                                <button type="button" disabled
                                  title="Přihlašovací e-mail se zatím posílá jen trenérům"
                                  aria-label="Přihlašovací e-mail se posílá jen trenérům"
                                  className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-300">
                                  <Mail className="h-4 w-4" />
                                </button>
                              );
                            }
                            return (
                              <button type="button" onClick={() => handleSendWelcome(u)} disabled={posila}
                                title={odeslano ? `Přihlašovací e-mail odeslán ${formatDateTime(u.welcomeEmailSentAt)}, klepnutím pošleš znovu` : "Poslat přihlašovací e-mail"}
                                aria-label={odeslano ? "Poslat přihlašovací e-mail znovu" : "Poslat přihlašovací e-mail"}
                                className={`flex h-9 w-9 items-center justify-center rounded-xl border disabled:opacity-50 ${
                                  odeslano ? "border-emerald-200 bg-emerald-50 text-emerald-600" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                                {posila ? <RefreshCw className="h-4 w-4 animate-spin" /> : odeslano ? <MailCheck className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                              </button>
                            );
                          })()}
                          <button type="button" onClick={() => setEditFor({ mode: "edit", user: u })}
                            title="Upravit" aria-label="Upravit"
                            className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50">
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => handleDelete(u.email)}
                            title="Smazat" aria-label="Smazat"
                            className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-400 hover:border-rose-200 hover:text-rose-600">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </Card>
        </>
      )}

      {tab === "alerts" && (
        <Card title="Pravidla pro alerty kampaní (Přehled)">
          <div className="text-sm text-slate-600 mb-4">
            Nastav kritéria, kdy appka upozorní na změnu výkonu kampaně.
          </div>
          <div className="space-y-3">
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="text-sm font-medium">Pravidlo 1: CPL nárůst</div>
              <div className="text-xs text-slate-500 mt-1">
                Pokud CPL vzroste o více než{" "}
                <input className="field mx-1 w-16" defaultValue="30" />%
                za posledních{" "}
                <input className="field mx-1 w-16" defaultValue="7" /> dní → warning
              </div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="text-sm font-medium">Pravidlo 2: Minimální doba běhu</div>
              <div className="text-xs text-slate-500 mt-1">
                Kampaň lze vyhodnotit až po{" "}
                <input className="field mx-1 w-16" defaultValue="30" /> dnech a{" "}
                <input className="field mx-1 w-16" defaultValue="500" /> leads
              </div>
            </div>
            <button className="px-4 py-2 bg-slate-900 text-white rounded-xl text-sm min-h-[44px] sm:min-h-0">+ Přidat pravidlo</button>
          </div>
        </Card>
      )}

      {tab === "trial" && <TrialAutomatKarta />}

      {tab === "fapi" && <FapiIgnorovaneKarta />}

      {tab === "boti" && <AiBotiKarta />}

      {tab === "integrations" && <TelegramKarta />}

      {tab === "integrations" && <SmartEmailingKarta />}

      {tab === "integrations" && (
        <Card title="API klíče & integrace">
          <div className="space-y-3">
            {[
              { name: "Meta Marketing API", status: "connected", detail: "Ad Account act_675744263063691" },
              { name: "Notion", status: "connected", detail: "Databáze Klienti (integrace: FW40 Bot)" },
              { name: "FAPI", status: "connected", detail: "Email: admin@fitway40.cz" },
              { name: "Reservio", status: "pending", detail: "Čeká na API klíč" },
              { name: "Gmail (banka)", status: "pending", detail: "Airbank + Raiffeisen emaily" },
            ].map((int) => (
              <div key={int.name} className="flex items-center justify-between p-4 border border-slate-200 rounded-xl flex-wrap gap-2">
                <div className="min-w-0">
                  <div className="font-medium">{int.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">{int.detail}</div>
                </div>
                <Pill tone={int.status === "connected" ? "green" : "amber"}>
                  {int.status === "connected" ? "Připojeno" : "Čeká"}
                </Pill>
              </div>
            ))}
          </div>
        </Card>
      )}

      {editFor && (
        <UserEditModal
          mode={editFor.mode}
          initialUser={editFor.user}
          onClose={() => setEditFor(null)}
          onSaved={() => { setEditFor(null); refetch(); }}
        />
      )}
    </div>
  );
}

// Modal pro vytvoření/editaci uživatele.
// Pole: email (jen create), role, status, name, linkedTrener.
function UserEditModal({ mode, initialUser, onClose, onSaved }) {
  const isCreate = mode === "create";
  const [form, setForm] = useState({
    email: initialUser?.email || "",
    role: initialUser?.role || "trener",
    status: initialUser?.status || "active",
    name: initialUser?.name || "",
    linkedTrener: initialUser?.linkedTrener || "",
    vedeLokalitu: initialUser?.vedeLokalitu || "",
  });
  const [phase, setPhase] = useState("input"); // "input" | "saving" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  // Seznam trenérů pro výběr "kterého trenéra účet vidí".
  const { data: trData } = useApiData("/api/trainers");
  const treneri = (trData?.trainers || [])
    .map((t) => t.jmeno)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, "cs"));
  // 3.9.2026: lokality na výběr, ne psaní rukou. Berou se z lokalit aktivních
  // trenérů, takže v nabídce je vždy jen to, co se opravdu používá.
  // 4.9.2026: přes lokalityProVyber, jinak se stejné místo psané dvěma způsoby
  // („VT GYM" vs „VT GYM - Kbely") nabízelo dvakrát.
  const lokality = lokalityProVyber((trData?.trainers || [])
    .filter((t) => String(t.status || "").toLowerCase() !== "neaktivní")
    .map((t) => t.lokalita));

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  // BC trenér se taky páruje na záznam člověka, jinak by mu nešlo přiřadit
  // lokalitu ani spočítat fakturaci (16.9.2026).
  const isTrenerRole = form.role === "trener" || form.role === "head-trener" || form.role === "bc_trener";
  // 6. 10. 2026 (Láďa): manažer, který sám trénuje (Michal), potřebuje napojení na trenéra
  // kvůli Mým tréninkům a zrcadlení trenérských úkolů do manažerské nástěnky.
  const maNapojeni = isTrenerRole || form.role === "manager";
  const canSubmit = emailValid && form.role && form.status;

  const submit = async () => {
    if (!canSubmit) return;
    setPhase("saving");
    setErrorMsg("");
    try {
      const r = await apiFetch("/api/admin-uzivatele", {
        method: "POST",
        body: JSON.stringify({
          action: isCreate ? "create" : "update",
          email: form.email.trim().toLowerCase(),
          role: form.role,
          status: form.status,
          name: form.name.trim() || null,
          // Propojení na trenéra dává smysl jen u rolí trenér / head-trenér; jinak null.
          linkedTrener: maNapojeni ? (form.linkedTrener.trim() || null) : null,
          // Head Coach Junior: trenérka, která navíc vede úkoly trenérů své lokality.
          vedeLokalitu: isTrenerRole ? (form.vedeLokalitu.trim() || null) : null,
        }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) {
        setErrorMsg(j.error || `HTTP ${r.status}`);
        setPhase("error");
        return;
      }
      onSaved();
    } catch (e) {
      setErrorMsg(e.message);
      setPhase("error");
    }
  };

  return (
    <div className="fixed inset-0 bg-brand-dark/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-bold text-slate-800">{isCreate ? "Přidat uživatele" : "Upravit uživatele"}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded min-h-[44px] sm:min-h-0"><XIcon className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          {phase === "error" && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-900">
              <b>Chyba:</b> {errorMsg}
            </div>
          )}
          <label className="block">
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Email *</span>
            <input
              type="email"
              value={form.email}
              onChange={setField("email")}
              disabled={!isCreate}
              placeholder="petr.janata@fitway40.cz"
              className="field mt-1 w-full disabled:bg-slate-100 disabled:text-slate-500"
              autoFocus={isCreate}
            />
            {!isCreate && <span className="text-xs text-slate-400">Email je PK, pro změnu použij Trenéři page → Změnit email.</span>}
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Jméno</span>
            <input
              type="text"
              value={form.name}
              onChange={setField("name")}
              placeholder="Petr Janata"
              className="field mt-1 w-full"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Role *</span>
              <select value={form.role} onChange={setField("role")} className="field mt-1 w-full">
                {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Status *</span>
              <select value={form.status} onChange={setField("status")} className="field mt-1 w-full">
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
          {/* Propojení na trenéra pro role trenér / head-trenér a pro manažera, který sám trénuje. */}
          {maNapojeni && (
            <label className="block">
              <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{form.role === "manager" ? "Trénuje jako (manažer, který sám trénuje)" : "Kterého trenéra účet vidí"}</span>
              <select
                value={form.linkedTrener}
                onChange={setField("linkedTrener")}
                className="field mt-1 w-full"
              >
                <option value="">Nepropojeno</option>
                {form.linkedTrener && !treneri.includes(form.linkedTrener) && (
                  <option value={form.linkedTrener}>{form.linkedTrener}</option>
                )}
                {treneri.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <span className="text-xs text-slate-500">Účet uvidí klienty, rozvrh a úkoly vybraného trenéra. Povinné.</span>
            </label>
          )}
          {/* 3.9.2026 Head Coach Junior: trenérka vede úkoly trenérů na lokalitě.
              Zůstává trenérkou, jen k tomu vidí a řídí úkoly kolegů. Sebe v tom
              přehledu nemá. Prázdné = běžný trenér. */}
          {isTrenerRole && (
            <label className="block">
              <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Vede úkoly trenérů na lokalitě</span>
              <select
                value={form.vedeLokalitu}
                onChange={setField("vedeLokalitu")}
                className="field mt-1 w-full"
              >
                <option value="">Nevede lokalitu</option>
                {form.vedeLokalitu && !lokality.includes(form.vedeLokalitu) && (
                  <option value={form.vedeLokalitu}>{form.vedeLokalitu}</option>
                )}
                {lokality.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <span className="text-xs text-slate-500">Uvidí úkoly trenérů této lokality kromě svých, může měnit stav i zadávat úkoly, a to i přes Jokyho ve Slacku.</span>
            </label>
          )}
        </div>
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded-xl text-sm hover:bg-slate-100 min-h-[44px] sm:min-h-0">Zrušit</button>
          {phase === "saving" ? (
            <div className="text-sm text-slate-500 flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Ukládám…</div>
          ) : (
            <button onClick={submit} disabled={!canSubmit} className="px-3 py-1.5 bg-slate-900 text-white rounded-xl text-sm hover:bg-slate-800 disabled:opacity-50 min-h-[44px] sm:min-h-0">
              {isCreate ? "Vytvořit" : "Uložit"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Telegram ────────────────────────────────────────────────────────────────
// Token i chat_id se ukládají do PG (app_config), ne do proměnných prostředí.
// Lambda má na proměnné dohromady 4 KB a ty jsou v adminu skoro vyčerpané.
// Token se ze serveru nikdy nevrací, jen jeho poslední čtyři znaky.
// 1. 11. 2026 (zadání 5): přihlašovací údaje ke SmartEmailing API pro blacklist při ukončení
// spolupráce „ne v dobrém“. Jen owner, ukládá se šifrovaně a zpátky se nikdy nezobrazí.
function SmartEmailingKarta() {
  const [stav, setStav] = useState(null);
  const [jmeno, setJmeno] = useState("");
  const [klic, setKlic] = useState("");
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  const nacti = async () => {
    try { setStav(await fetchApi("/api/smartemailing-nastaveni")); } catch (e) { setChyba(e.message || "Načtení selhalo."); }
  };
  React.useEffect(() => { nacti(); }, []); // eslint-disable-line
  const uloz = async () => {
    setBusy(true); setChyba("");
    try {
      const r = await apiFetch("/api/smartemailing-nastaveni", { method: "POST", body: JSON.stringify({ username: jmeno.trim(), apiKey: klic.trim() }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Chyba ${r.status}`);
      setStav(j); setJmeno(""); setKlic("");
      toast("Údaje ke SmartEmailingu jsou ověřené a uložené.", "success");
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  const ulozeno = !!stav?.ulozeno && stav?.citelne !== false;
  return (
    <Card title="SmartEmailing (blacklist při ukončení)" className="mb-4">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className={`inline-flex rounded-full px-2.5 py-1 text-[12.5px] font-bold ${ulozeno ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
          {ulozeno ? "Uloženo" : stav?.ulozeno ? "Uloženo, ale nejde přečíst, vlož znovu" : "Nenapojeno"}
        </span>
        {stav?.zmeneno_at && <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-[12.5px] font-bold text-slate-600">{formatDateTime(stav.zmeneno_at)}</span>}
        {stav?.zmenil && <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-[12.5px] font-bold text-slate-600">{stav.zmenil}</span>}
      </div>
      <div className="text-[13px] text-slate-600 mb-3">Při ukončení spolupráce ne v dobrém jde klientku jedním zaškrtnutím přidat na blacklist. Údaje najdeš ve SmartEmailingu v části Můj účet, API klíče. Po uložení se už nikdy nezobrazí{ulozeno ? ", vyplněním se přepíšou" : ""}.</div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Field label="Uživatelské jméno (přihlašovací e-mail)">
          <input className="field w-full" value={jmeno} onChange={(e) => setJmeno(e.target.value)} autoComplete="off" placeholder="jmeno@firma.cz" />
        </Field>
        <Field label="API klíč">
          <input className="field w-full" type="password" value={klic} onChange={(e) => setKlic(e.target.value)} autoComplete="new-password" placeholder="API klíč" />
        </Field>
      </div>
      {chyba && <div className="mt-3 rounded-xl bg-rose-50 px-3 py-2 text-[13px] font-semibold text-rose-800">{chyba}</div>}
      <button type="button" className="btn btn-primary mt-3 w-full sm:w-auto" disabled={busy || !jmeno.trim() || !klic.trim()} onClick={uloz}>
        {busy ? "Ověřuji…" : "Ověřit a uložit"}
      </button>
    </Card>
  );
}

function TelegramKarta() {
  const [stav, setStav] = useState(null);
  const [nacitam, setNacitam] = useState(false);
  const [token, setToken] = useState("");
  const [chat, setChat] = useState("");
  const [busy, setBusy] = useState(false);

  const nacti = async () => {
    setNacitam(true);
    try {
      const j = await fetchApi("/api/telegram-setup");
      setStav(j);
      if (j?.chatBootcampu) setChat(String(j.chatBootcampu));
    } catch (e) {
      toast(e.message || "Načtení selhalo.", "error");
    } finally {
      setNacitam(false);
    }
  };

  React.useEffect(() => { nacti(); }, []); // eslint-disable-line

  const posli = async (telo, hlaska) => {
    setBusy(true);
    try {
      const j = await fetchApi("/api/telegram-setup", { method: "POST", body: JSON.stringify(telo) });
      toast(hlaska, "success");
      if (telo.token !== undefined) setToken("");
      await nacti();
      return j;
    } catch (e) {
      toast(e.message || "Nepodařilo se to.", "error");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const hotovo = !!stav?.hotovo;

  return (
    <Card title="Telegram (oznámení do skupin)" className="mb-4">
      <div className="flex items-center gap-2 mb-4">
        <span className={`inline-flex px-2.5 py-1 rounded-full text-[12.5px] font-medium ${
          hotovo ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
          {hotovo ? "Napojeno" : "Nenapojeno"}
        </span>
        {stav?.tokenKonec && <span className="text-[13px] text-slate-500">token {stav.tokenKonec}</span>}
        {stav?.chatBootcampu && <span className="text-[13px] text-slate-500">skupina {stav.chatBootcampu}</span>}
        <button type="button" onClick={nacti} disabled={nacitam}
          className="ml-auto inline-flex items-center gap-1.5 min-h-[34px] px-3 py-1 rounded-full border border-slate-200 text-slate-600 text-[13px] hover:bg-slate-50">
          <RefreshCw className={`w-3.5 h-3.5 ${nacitam ? "animate-spin" : ""}`} /> Načíst znovu
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1.5">Token bota od @BotFather</label>
          <div className="flex gap-2">
            <input type="password" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off"
              placeholder="8123456789:AAF..."
              className="field flex-1" />
            <button type="button" disabled={busy || !token.trim()}
              onClick={() => posli({ token: token.trim() }, "Token uložen.")}
              className="btn btn-primary">
              Uložit
            </button>
          </div>
          <div className="text-[12.5px] text-slate-500 mt-1.5">Zpátky se nikdy nevypisuje, jen poslední čtyři znaky.</div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1.5">chat_id skupiny bootcampu</label>
          <div className="flex gap-2">
            <input value={chat} onChange={(e) => setChat(e.target.value)} placeholder="-1001234567890"
              className="field flex-1" />
            <button type="button" disabled={busy}
              onClick={() => posli({ chat: chat.trim() }, "Skupina uložena.")}
              className="btn btn-primary">
              Uložit
            </button>
          </div>
          <div className="text-[12.5px] text-slate-500 mt-1.5">U skupin je to záporné číslo.</div>
        </div>
      </div>

      {!!stav?.chaty?.length && (
        <div className="mt-4">
          <div className="text-xs font-medium text-slate-500 mb-1.5">Skupiny, které bot vidí</div>
          <div className="flex flex-wrap gap-2">
            {stav.chaty.map((c) => (
              <button key={c.id} type="button" onClick={() => setChat(c.id)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 text-slate-700 text-[13px] hover:bg-slate-50">
                {c.nazev || c.typ} <span className="text-slate-400 tabular-nums">{c.id}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {stav?.poznamka && <div className="mt-3 text-[13px] text-amber-700">{stav.poznamka}</div>}

      <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-2">
        <button type="button" disabled={busy || !hotovo}
          onClick={() => posli({ test: 1 }, "Zkušební zpráva odešla.")}
          className="inline-flex items-center gap-1.5 min-h-[34px] px-3 py-1.5 rounded-full border border-slate-200 text-slate-700 text-[13px] hover:bg-slate-50 disabled:opacity-50">
          Poslat zkušební zprávu
        </button>
        <button type="button" disabled={busy}
          onClick={async () => {
            const j = await posli({ nahled: 1 }, "Náhled hotový.");
            if (j?.nahled) toast(`Prázdných lekcí po uzávěrce: ${j.nahled.prazdnych}. ${(j.nahled.chyby || []).join(" ")}`, "success");
          }}
          className="inline-flex items-center gap-1.5 min-h-[34px] px-3 py-1.5 rounded-full border border-slate-200 text-slate-700 text-[13px] hover:bg-slate-50 disabled:opacity-50">
          Náhled, co by dnes odešlo
        </button>
      </div>

      {!!stav?.navod?.length && (
        <ol className="mt-4 text-[13px] text-slate-500 space-y-1 list-decimal list-inside">
          {stav.navod.map((k, i) => <li key={i}>{String(k).replace(/^\d+\.\s*/, "")}</li>)}
        </ol>
      )}
    </Card>
  );
}

// ─── Trial automat ───────────────────────────────────────────────────────────
// Samoobslužná rezervace zkušebního tréninku z webu (2. 10. 2026). Zájemkyně si
// po dotazníku vybere termín, ten se jí drží jako čekající rezervace, a zaplatí
// kartou ve formuláři FAPI. Vypínač je tady, protože sales do toho sahat nemá.
function TrialAutomatKarta() {
  const { data, loading, refetch } = useApiData("/api/sales?akce=trial_automat");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const n = form || data?.nastaveni || null;
  const zmen = (k, v) => setForm({ ...(form || data?.nastaveni || {}), [k]: v });

  const uloz = async (patch) => {
    setBusy(true);
    try {
      const r = await apiFetch("/api/sales", { method: "POST", body: JSON.stringify({ akce: "trial_automat", nastaveni: patch || form || {} }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) { toast(j.error || `Uložení selhalo (HTTP ${r.status})`, "error"); return; }
      setForm(null); refetch();
      toast("Nastavení uloženo.", "success");
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); }
    finally { setBusy(false); }
  };

  if (loading || !n) return <Card title="Trial automat"><div className="text-sm text-slate-500">Načítám…</div></Card>;

  return (
    <div className="space-y-4">
      <Card title="Trial automat">
        <div className="text-sm text-slate-600">
          Zájemkyně si po odeslání dotazníku sama vybere termín zkušebního tréninku a zaplatí ho kartou.
          Když automat vypnete, web po dotazníku jen poděkuje a kontakt jde na sales k zavolání.
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-brand/20 bg-brand-light/50 p-4">
          <div>
            <div className="font-semibold">Rezervace z webu</div>
            <div className="text-[13px] text-slate-500">Změna platí hned, web si stav tahá z adminu.</div>
          </div>
          <button type="button" disabled={busy}
            onClick={() => uloz({ ...(form || n), zapnuto: !n.zapnuto })}
            className={n.zapnuto ? "btn-primary btn-sm" : "btn-secondary btn-sm"}>
            {n.zapnuto ? "Zapnuto" : "Vypnuto"}
          </button>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Cena zkušebního tréninku" hint="Mění se v nastavení sales, tady jen pro kontrolu.">
            <input className="field" value={`${n.cena} Kč`} readOnly />
          </Field>
          <Field label="Pobočka" hint="Kam se zájemkyně objednává.">
            <select className="field" value={n.lokalita} onChange={(e) => zmen("lokalita", e.target.value)}>
              {(data?.lokality || []).map((l) => <option key={l.kod} value={l.kod}>{l.nazev || l.kratky_nazev || l.kod}</option>)}
            </select>
          </Field>
          <Field label="Držení termínu před platbou" hint="Minuty, po které termín nikdo jiný nedostane.">
            <input className="field" type="number" min="5" max="60" value={n.drzeni_min} onChange={(e) => zmen("drzeni_min", Number(e.target.value))} />
          </Field>
          <Field label="Kolik týdnů dopředu nabízet" hint="Nabídka vždy začíná pondělím příštího týdne.">
            <input className="field" type="number" min="1" max="8" value={n.tydnu_dopredu} onChange={(e) => zmen("tydnu_dopredu", Number(e.target.value))} />
          </Field>
          <Field label="ID formuláře FAPI" hint="Formulář Zkušební trénink 490 Kč, Trial automat (jen platba kartou).">
            <input className="field" value={n.fapi_form || ""} onChange={(e) => zmen("fapi_form", e.target.value)} />
          </Field>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" className="btn-primary btn-sm" disabled={busy || !form} onClick={() => uloz()}>Uložit</button>
          {form && <button type="button" className="btn-ghost btn-sm" disabled={busy} onClick={() => setForm(null)}>Zrušit změny</button>}
        </div>
      </Card>

      <Card title="Klíč pro web">
        <div className="text-sm text-slate-600">
          Tímhle klíčem se fitway40.cz hlásí do adminu. Patří do proměnné <code className="rounded bg-slate-100 px-1.5 py-0.5">TRIAL_AUTOMAT_SECRET</code> v Netlify u webu, nikam jinam.
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <code className="rounded-xl bg-slate-100 px-3 py-2 text-[13px] break-all">{data?.klic || "zatím nic"}</code>
          <button type="button" className="btn-secondary btn-sm"
            onClick={async () => { try { await navigator.clipboard.writeText(String(data?.klic || "")); toast("Klíč zkopírován.", "success"); } catch { toast("Zkopírovat se nepodařilo.", "error"); } }}>
            Kopírovat
          </button>
        </div>
      </Card>

      <Card title="Jak to běží">
        <ul className="text-[13px] text-slate-600 space-y-2">
          <li>Jeden zkušební trénink na jednu ženu. Kdo už ho má za sebou, uvidí na webu vzkaz a spadne sales k zavolání.</li>
          <li>Nabídka termínů počítá to samé co sales: pracovní okna trenérů, absence, 90 minut vcelku a žádné dva zkušební na pobočce blíž než 30 minut.</li>
          <li>Nezaplacené držení se samo uvolní, hlídá to i hodinový cron.</li>
          <li>Po zaplacení jde zájemkyni e-mail s informacemi a pozvánkou, trenérovi pozvánka a do Slacku zpráva do kanálu pobočky.</li>
        </ul>
      </Card>
    </div>
  );
}

// FW-153 (14. 10. 2026): faktury a položky FAPI, které hlídač „Neznámé položky FAPI"
// nemá hlásit a nepočítají se jako tréninky. Přidává se z odkazu ve zprávě Jokyho
// (?tab=fapi&ignorovat=faktura|polozka&ucet=&cislo=&polozka=&klient=), vrací tlačítkem Vrátit.
function FapiIgnorovaneKarta() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading, refetch } = useApiData("/api/fapi-ignorovane", { bezKese: true });
  const polozky = data?.polozky || [];
  const [busy, setBusy] = useState(false);
  const [vratit, setVratit] = useState(null);
  const [volba, setVolba] = useState("faktura");
  const pozadavek = (() => {
    const typ = searchParams.get("ignorovat");
    // 15. 10. 2026: ze Slacku chodí jedno tlačítko Ignorovat (ignorovat=vyber), volba
    // faktura nebo položka se dělá tady. Starší odkazy faktura/polozka fungují dál.
    if (typ !== "faktura" && typ !== "polozka" && typ !== "vyber") return null;
    return { typ, ucet: searchParams.get("ucet") || "", cislo: searchParams.get("cislo") || "", polozka: searchParams.get("polozka") || "", klient: searchParams.get("klient") || "" };
  })();
  const zavritPozadavek = () => setSearchParams((sp) => { ["ignorovat", "ucet", "cislo", "polozka", "klient"].forEach((k) => sp.delete(k)); return sp; }, { replace: true });
  const posli = async (telo, hlaska) => {
    setBusy(true);
    try {
      await fetchApi("/api/fapi-ignorovane", { method: "POST", body: JSON.stringify(telo) });
      toast(hlaska, "success");
      await refetch();
    } catch (e) {
      toast(e.message || "Nepodařilo se to.", "error");
    } finally { setBusy(false); }
  };
  const typPozadavku = pozadavek ? (pozadavek.typ === "vyber" ? volba : pozadavek.typ) : null;
  const moznost = (hodnota, nadpis, pod) => (
    <button type="button" onClick={() => setVolba(hodnota)} aria-pressed={volba === hodnota}
      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left ${volba === hodnota ? "border-2 border-brand bg-brand-50" : "border border-slate-200 bg-white"}`}>
      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${volba === hodnota ? "border-brand" : "border-slate-300"}`}>{volba === hodnota && <span className="h-2.5 w-2.5 rounded-full bg-brand" />}</span>
      <span className="min-w-0"><span className="block text-sm font-bold text-slate-900">{nadpis}</span><span className="block text-xs text-slate-500">{pod}</span></span>
    </button>
  );
  const popisPozadavku = pozadavek && pozadavek.typ === "vyber"
    ? (
      <div className="space-y-3">
        <div className="demo-sensitive">{[`${pozadavek.ucet} ${pozadavek.cislo}`.trim(), pozadavek.klient, pozadavek.polozka].filter(Boolean).join(", ")}</div>
        <div className="space-y-2">
          {moznost("faktura", "Jen tuhle fakturu", `„${pozadavek.polozka}“ jinde se hlásit bude dál`)}
          {moznost("polozka", `Položku „${pozadavek.polozka}“ vždy`, "Na všech fakturách")}
        </div>
        <div className="text-xs text-slate-500">Nebude se počítat jako tréninky a Joky to přestane hlásit. Jde to kdykoli vrátit tady v Nastavení.</div>
      </div>
    )
    : pozadavek
    ? (pozadavek.typ === "faktura"
      ? `${pozadavek.ucet} ${pozadavek.cislo}, ${pozadavek.klient}, ${pozadavek.polozka}. Faktura se nebude počítat jako tréninky a Joky ji přestane hlásit. Jde to kdykoli vrátit tady v Nastavení.`
      : `Položka „${pozadavek.polozka}" se nebude počítat jako tréninky na žádné faktuře a Joky ji přestane hlásit. Jde to kdykoli vrátit tady v Nastavení.`)
    : "";
  const datum = (iso) => (iso ? new Date(iso).toLocaleDateString("cs-CZ", { timeZone: "Europe/Prague" }) : "");
  return (
    <>
      <Card title="Ignorované ve FAPI hlídači" lista ikona={EyeOff} odznak={polozky.length || undefined}>
        <div className="mb-2 text-sm text-slate-500">Tyto faktury a položky se nepočítají jako tréninky a hlídač „Neznámé položky FAPI“ je už nehlásí.</div>
        {loading && !data ? <div className="py-4 text-sm text-slate-400">Načítám…</div> : polozky.length === 0 ? (
          <div className="py-4 text-sm text-slate-500">Zatím nic. Ignorovat jde odkazem ve zprávě Jokyho o neznámé položce.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {polozky.map((r) => (
              <div key={r.id} className="flex min-h-[56px] flex-wrap items-center gap-3 py-2.5">
                <span className={`rounded-full px-2.5 py-1 text-[13px] font-bold ${r.typ === "faktura" ? "bg-brand-100 text-brand-deep" : "bg-sky-100 text-sky-800"}`}>{r.typ === "faktura" ? "Faktura" : "Položka"}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-base font-bold text-slate-900 demo-sensitive">{r.typ === "faktura" ? `${r.ucet} ${r.cislo}` : `„${r.popis || r.polozka}“ na všech fakturách`}</div>
                  <div className="flex flex-wrap items-center gap-x-2 text-[13px] text-slate-500">
                    {r.typ === "faktura" && r.popis && <><span className="demo-sensitive">{r.popis}</span><span aria-hidden className="h-3 w-px bg-slate-300" /></>}
                    <span>{r.kdo || "?"}, {datum(r.created_at)}</span>
                  </div>
                </div>
                <button type="button" disabled={busy} onClick={() => setVratit(r)} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                  <RotateCcw className="h-4 w-4" /> Vrátit
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
      <ConfirmDialog
        open={!!pozadavek}
        title={pozadavek?.typ === "vyber" ? "Ignorovat ve FAPI hlídači?" : pozadavek?.typ === "polozka" ? "Ignorovat položku vždy?" : "Ignorovat fakturu?"}
        description={popisPozadavku}
        confirmLabel="Ignorovat"
        variant="primary"
        onConfirm={async () => {
          const p = { ...pozadavek, typ: typPozadavku };
          zavritPozadavek();
          setVolba("faktura");
          await posli(p.typ === "faktura"
            ? { action: "pridat", typ: "faktura", ucet: p.ucet, cislo: p.cislo, popis: [p.klient, p.polozka].filter(Boolean).join(", ") }
            : { action: "pridat", typ: "polozka", polozka: p.polozka, popis: p.polozka }, "Ignorování uloženo.");
        }}
        onCancel={() => { zavritPozadavek(); setVolba("faktura"); }}
      />
      <ConfirmDialog
        open={!!vratit}
        title="Vrátit do hlídače?"
        description={vratit ? `${vratit.typ === "faktura" ? `Faktura ${vratit.ucet} ${vratit.cislo}` : `Položka „${vratit.popis || vratit.polozka}“`} se zase bude počítat a hlídač ji bude hlásit, pokud ji nepozná.` : ""}
        confirmLabel="Vrátit"
        variant="warning"
        onConfirm={async () => { const r = vratit; setVratit(null); await posli({ action: "odebrat", id: r.id }, "Vráceno do hlídače."); }}
        onCancel={() => setVratit(null)}
      />
    </>
  );
}
